import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

import { SuggestionOption } from '../types';
import { filterSuggestions, groupSuggestionsByCategory } from '../utils';

interface UseAutocompleteStateProps {
  value: string;
  suggestions: SuggestionOption[];
  minSearchLength: number;
  maxSuggestions: number;
  showOnFocus: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  lastSelectedValue?: string | null;
  enableSpecialCharacters?: boolean;
}

interface UseAutocompleteStateReturn {
  isOpen: boolean;
  highlightedIndex: number;
  filteredSuggestions: SuggestionOption[];
  groupedSuggestions: ReturnType<typeof groupSuggestionsByCategory>;
  isLoading: boolean;
  error: Error | null;
  setIsOpen: (isOpen: boolean) => void;
  setHighlightedIndex: (index: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  reset: () => void;
}

export const useAutocompleteState = ({
  value,
  suggestions,
  minSearchLength,
  maxSuggestions,
  showOnFocus,
  inputRef,
  lastSelectedValue,
  enableSpecialCharacters = true,
}: UseAutocompleteStateProps): UseAutocompleteStateReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Check if query is a domain shortcut query
  const isSpecialQuery = useMemo(() => {
    if (!enableSpecialCharacters || !value) return false;
    return value.startsWith('!');
  }, [value, enableSpecialCharacters]);

  // Debounce the input value to prevent excessive filtering during fast typing
  // Only debounce for non-special queries to maintain responsiveness for shortcuts
  const [debouncedValue] = useDebounce(value, isSpecialQuery ? 0 : 300);

  // Filter suggestions based on current input value
  const filteredSuggestions = useMemo(() => {
    try {
      setError(null); // Clear any previous errors

      let baseSuggestions: SuggestionOption[] = [];

      // Use debounced value for filtering to improve performance
      const valueToFilter = isSpecialQuery ? value : debouncedValue;

      // Special character queries are now handled at the data source level
      // The data source already applies proper filtering, so we don't need additional filtering
      if (isSpecialQuery) {
        // For special character queries, the suggestions are already processed and filtered by data sources
        // Don't apply additional filtering as it causes incorrect results
        baseSuggestions = suggestions;
      } else {
        // Standard filtering behavior for non-special character queries
        if (showOnFocus) {
          // If user hasn't typed enough to filter, show all suggestions
          if (valueToFilter.length < minSearchLength) {
            baseSuggestions = suggestions;
          } else {
            // If user has typed enough, show filtered suggestions
            baseSuggestions = filterSuggestions(suggestions, valueToFilter);
          }
        } else {
          // Standard behavior: only show suggestions when minSearchLength is met
          baseSuggestions =
            valueToFilter.length >= minSearchLength
              ? filterSuggestions(suggestions, valueToFilter)
              : [];
        }
      }

      return baseSuggestions.slice(0, maxSuggestions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to filter suggestions'));
      return [];
    }
  }, [
    suggestions,
    value,
    debouncedValue,
    minSearchLength,
    maxSuggestions,
    showOnFocus,
    isSpecialQuery,
  ]);

  // Group suggestions by category for display
  const groupedSuggestions = useMemo(() => {
    try {
      return groupSuggestionsByCategory(filteredSuggestions);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to group suggestions'));
      return [];
    }
  }, [filteredSuggestions]);

  // Reset highlighted index when suggestions change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredSuggestions]);

  // Manage dropdown open/close state based on suggestions availability
  useEffect(() => {
    if (filteredSuggestions.length === 0 && !isLoading) {
      // Close dropdown when no suggestions and not loading
      setIsOpen(false);
    } else if (filteredSuggestions.length > 0 && !isLoading) {
      // Don't open if the current value is the same as what was just selected
      if (lastSelectedValue && value === lastSelectedValue) {
        return;
      }

      // Open dropdown when suggestions become available AND the specific input is focused
      // This fixes the backspace bug by checking if our specific autocomplete input is focused
      const isInputFocused = inputRef?.current && document.activeElement === inputRef.current;

      if (isInputFocused) {
        setIsOpen(true);
      }
    }
  }, [filteredSuggestions.length, isLoading, inputRef, lastSelectedValue, value]);

  // Reset function to clear all state
  const reset = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setLoading(false);
    setError(null);
  }, []);

  return {
    isOpen,
    highlightedIndex,
    filteredSuggestions,
    groupedSuggestions,
    isLoading,
    error,
    setIsOpen,
    setHighlightedIndex,
    setLoading,
    setError,
    reset,
  };
};
